import type { ReactElement } from 'react'
import { AppShell } from '../components/AppShell.js'
import { Button } from '../components/ui/button.js'
import { Icon } from '../components/installer/icons.js'
import { ANCHOR_FAIL, PROOF_STEPS } from '../lib/proofSteps.js'
import { useDemoStore } from '../state/store.js'
import { useStep } from '../state/useStep.js'

/**
 * Bad-path verification result for the anchor step. Reached only on the first
 * `anchor` capture attempt; the "Recapture anchor strap" CTA routes back to
 * the camera, where the retake passes.
 */
export const VerifyFailedScreen = (): ReactElement => {
  const { state } = useDemoStore()
  const { goto } = useStep()

  const progress = (30 + ((state.activeIdx + 1) / PROOF_STEPS.length) * 60) / 100

  // For the video shot, freeze the fail clip's poster — the red box is burned in, so
  // no overlay is drawn (`failBox` stays undefined). Falls back to the still + box.
  const failImage = ANCHOR_FAIL.video !== undefined ? ANCHOR_FAIL.video.poster : ANCHOR_FAIL.photo
  const failBox = ANCHOR_FAIL.video !== undefined ? undefined : ANCHOR_FAIL.cameraBox

  const footer = (
    <Button size="xl" onClick={() => goto('capture')}>
      <Icon.Camera /> Recapture anchor strap
    </Button>
  )

  return (
    <AppShell
      title="Verification failed"
      showBack
      onBack={() => goto('capture')}
      progress={progress}
      footer={footer}
    >
      <div className="installer-surface page fade-in">
        <div className="verify-image-card">
          <div
            className="verify-image"
            style={{ backgroundImage: `url(${failImage})` }}
          >
            {failBox !== undefined && (
              <div
                className="detection-box-fail"
                style={{
                  top: failBox.top,
                  left: failBox.left,
                  width: failBox.width,
                  height: failBox.height,
                }}
              >
                <div className="detection-label-fail">{failBox.text}</div>
              </div>
            )}
          </div>
        </div>

        <div className="signal-chip fail">
          <div className="signal-dot" />
          Defect found
        </div>

        <div className="outcome-card fail">
          <div className="outcome-title">{ANCHOR_FAIL.outcomeTitle}</div>
          <div className="outcome-body">{ANCHOR_FAIL.outcomeBody}</div>
        </div>
      </div>
    </AppShell>
  )
}
